// Firestore side of Agency SOPs.
//   sopAgencies/{agencyId}                      global SOP per agency, plus 'common'
//   projects/{projectId}/sopOverrides/{agencyId} a project's added and turned-off points
//   sopLessons/{lessonId}                       site lessons in any language, reviewed into SOPs
//   sopPdfs/{pdfId}                             every bilingual PDF generated, with its version
import { addDoc, collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, runTransaction, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { db, functions, storage } from '../firebase';
import {
  COMMON_SOP_ID, draftSummary, EMPTY_OVERRIDE, publishBlockers, pushLog, startDraft, starterSops,
  type SopAgency, type SopCheckAnswer, type SopLesson, type SopOverride, type SopPoint, type SopProposal, type SopRateRow,
} from './sops';

export const SOP_AGENCY_COLLECTION = 'sopAgencies';
export const SOP_LESSON_COLLECTION = 'sopLessons';
const overridesCol = (projectId: string) => collection(db, 'projects', projectId, 'sopOverrides');

function toAgency(id: string, raw: any): SopAgency {
  return {
    id,
    name: String(raw?.name || id),
    nameGu: raw?.nameGu ? String(raw.nameGu) : '',
    order: Number(raw?.order ?? 999),
    archived: !!raw?.archived,
    projects: Array.isArray(raw?.projects) ? raw.projects.map(String) : 'all',
    version: Number(raw?.version || 1),
    clauses: Array.isArray(raw?.clauses) ? raw.clauses : [],
    log: Array.isArray(raw?.log) ? raw.log : [],
    samples: raw?.samples && typeof raw.samples === 'object' ? raw.samples : {},
    checklist: Array.isArray(raw?.checklist) ? raw.checklist : [],
    ...(raw?.siteTeam ? { siteTeam: true } : {}),
    ...(raw?.group ? { group: String(raw.group) } : {}),
    draft: raw?.draft && Array.isArray(raw.draft.clauses)
      ? { clauses: raw.draft.clauses, basedOn: Number(raw.draft.basedOn || raw?.version || 1), notes: Array.isArray(raw.draft.notes) ? raw.draft.notes : [] }
      : null,
    review: raw?.review && Array.isArray(raw.review.proposals)
      ? { proposals: raw.review.proposals, at: String(raw.review.at || ''), by: String(raw.review.by || '') }
      : null,
  };
}

function toOverride(raw: any): SopOverride {
  return {
    added: Array.isArray(raw?.added) ? raw.added : [],
    off: raw?.off && typeof raw.off === 'object' ? raw.off : {},
  };
}

/** Every agency SOP and the Common SOP, in the admin's order. */
export function useSopAgencies(): { agencies: SopAgency[]; loading: boolean; error: string } {
  const [state, setState] = useState<{ agencies: SopAgency[]; loading: boolean; error: string }>({ agencies: [], loading: true, error: '' });
  useEffect(() => onSnapshot(collection(db, SOP_AGENCY_COLLECTION), (snapshot) => {
    const agencies = snapshot.docs.map((item) => toAgency(item.id, item.data()))
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
    setState({ agencies, loading: false, error: '' });
  }, (err) => setState({ agencies: [], loading: false, error: err?.message || 'Could not load SOPs.' })), []);
  return state;
}

/** The current project's changes, keyed by agency id. */
export function useSopOverrides(projectId: string | null | undefined): Record<string, SopOverride> {
  const [byAgency, setByAgency] = useState<Record<string, SopOverride>>({});
  useEffect(() => {
    if (!projectId) { setByAgency({}); return undefined; }
    return onSnapshot(overridesCol(projectId), (snapshot) => {
      const next: Record<string, SopOverride> = {};
      snapshot.docs.forEach((item) => { next[item.id] = toOverride(item.data()); });
      setByAgency(next);
    }, () => setByAgency({}));
  }, [projectId]);
  return byAgency;
}

export function useSopLessons(): SopLesson[] {
  const [lessons, setLessons] = useState<SopLesson[]>([]);
  useEffect(() => onSnapshot(query(collection(db, SOP_LESSON_COLLECTION), orderBy('createdAt', 'desc'), limit(500)), (snapshot) => {
    setLessons(snapshot.docs.map((item) => {
      const raw: any = item.data();
      return {
        id: item.id,
        agencyId: String(raw.agencyId || ''),
        projectId: String(raw.projectId || ''),
        vendor: raw.vendor ? String(raw.vendor) : '',
        impact: raw.impact || 'rework',
        what: String(raw.what || ''),
        suggest: raw.suggest ? String(raw.suggest) : '',
        status: raw.status || 'new',
        result: raw.result ? String(raw.result) : '',
        en: raw.en ? String(raw.en) : '',
        date: String(raw.date || ''),
        createdByName: raw.createdByName ? String(raw.createdByName) : '',
      } as SopLesson;
    }));
  }, () => setLessons([])), []);
  return lessons;
}

/** Writes the starter SOPs for agencies that do not exist yet. Existing SOPs are never touched. */
export async function seedStarterSops(byName: string): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;
  for (const rec of starterSops()) {
    const ref = doc(db, SOP_AGENCY_COLLECTION, rec.id);
    if ((await getDoc(ref)).exists()) continue;
    batch.set(ref, { ...rec, log: [{ v: 1, note: 'Starter SOP loaded', at: new Date().toISOString(), by: byName }], updatedAt: serverTimestamp() });
    count++;
  }
  if (count) await batch.commit();
  return count;
}

/** Read-modify-write one agency SOP. Return null from mutate to write nothing. */
export async function updateAgency(agencyId: string, mutate: (agency: SopAgency) => SopAgency | null, byName: string): Promise<SopAgency | null> {
  const ref = doc(db, SOP_AGENCY_COLLECTION, agencyId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('This SOP no longer exists.');
    const next = mutate(toAgency(agencyId, snap.data()));
    if (!next) return null;
    const rest: Partial<SopAgency> = { ...next };
    delete rest.id;
    tx.set(ref, { ...rest, updatedAt: serverTimestamp(), updatedBy: byName });
    return next;
  });
}

/** Change the draft of an agency SOP, starting one from the approved SOP when there is none. */
export async function editDraft(agencyId: string, mutate: (clauses: SopPoint[]) => SopPoint[], note: string, byName: string): Promise<void> {
  await updateAgency(agencyId, (a) => {
    const draft = startDraft(a);
    return { ...a, draft: { ...draft, clauses: mutate(draft.clauses), notes: [...draft.notes, note].slice(-200) } };
  }, byName);
}

/** Make the draft the approved SOP. Refused while any point lacks checked Gujarati. */
export async function publishDraft(agencyId: string, byName: string): Promise<number> {
  const next = await updateAgency(agencyId, (a) => {
    if (!a.draft) throw new Error('There is no draft to publish.');
    const blockers = publishBlockers(a.draft.clauses);
    if (blockers.length) throw new Error(`${blockers.length} point${blockers.length === 1 ? ' needs' : 's need'} checked Gujarati before publishing.`);
    const s = draftSummary(a);
    const version = a.version + 1;
    const note = `Published: ${s.added} added, ${s.changed} changed, ${s.removed} removed`;
    return { ...a, version, clauses: a.draft.clauses, draft: null, log: pushLog(a.log, { v: version, note, at: new Date().toISOString(), by: byName }) };
  }, byName);
  return next?.version || 0;
}

export async function discardDraft(agencyId: string, byName: string): Promise<void> {
  await updateAgency(agencyId, (a) => ({ ...a, draft: null }), byName);
}

export async function saveReview(agencyId: string, proposals: SopProposal[] | null, byName: string): Promise<void> {
  await updateAgency(agencyId, (a) => ({ ...a, review: proposals ? { proposals, at: new Date().toISOString(), by: byName } : null }), byName);
}

export async function createAgency(agency: SopAgency, byName: string): Promise<void> {
  const { id, ...rest } = agency;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, SOP_AGENCY_COLLECTION, id);
    if ((await tx.get(ref)).exists()) throw new Error('An agency with this id already exists.');
    tx.set(ref, { ...rest, updatedAt: serverTimestamp(), updatedBy: byName });
  });
}

/** Read-modify-write one project's changes for one agency. */
export async function updateOverride(projectId: string, agencyId: string, mutate: (o: SopOverride) => SopOverride, byName: string): Promise<void> {
  const ref = doc(overridesCol(projectId), agencyId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const next = mutate(snap.exists() ? toOverride(snap.data()) : { ...EMPTY_OVERRIDE, off: {} });
    tx.set(ref, { added: next.added, off: next.off, updatedAt: serverTimestamp(), updatedBy: byName });
  });
}

export async function createLesson(lesson: Omit<SopLesson, 'id' | 'status' | 'date'>, uid: string): Promise<void> {
  await addDoc(collection(db, SOP_LESSON_COLLECTION), {
    agencyId: lesson.agencyId, projectId: lesson.projectId, vendor: lesson.vendor || '', impact: lesson.impact,
    what: lesson.what, suggest: lesson.suggest || '', status: 'new', date: new Date().toISOString().slice(0, 10),
    createdBy: uid, createdByName: lesson.createdByName || '', createdAt: serverTimestamp(),
  });
}

export async function updateLesson(id: string, patch: Partial<Pick<SopLesson, 'status' | 'result' | 'en'>>): Promise<void> {
  await updateDoc(doc(db, SOP_LESSON_COLLECTION, id), { ...patch, updatedAt: serverTimestamp() });
}

// ---------- Translation (Gemini on Vertex AI, through the sopTranslate Cloud Function) ----------

type DraftPoint = { id: string; section: number; text: string; hold: boolean };
type TranslateRequest =
  | { mode: 'toGu' | 'toEn'; texts: string[] }
  | { mode: 'lessonToEn'; text: string }
  | { mode: 'formalize'; agencyName: string; what: string; suggest?: string }
  | { mode: 'rewrite'; agencyName: string; en: string; gu: string }
  | { mode: 'draftSop'; agencyName: string; current: DraftPoint[]; common: DraftPoint[]; pending: Array<{ id: string; text: string; context?: string; impact?: string; vendor?: string }> };
const translateFn = () => httpsCallable<TranslateRequest, any>(functions, 'sopTranslate');

export async function translateTexts(texts: string[], to: 'gu' | 'en'): Promise<string[]> {
  const res = await translateFn()({ mode: to === 'gu' ? 'toGu' : 'toEn', texts });
  const out = res.data?.texts;
  if (!Array.isArray(out) || out.length !== texts.length) throw new Error('Translation came back incomplete. Try again.');
  return out.map((s: unknown) => String(s || '').trim());
}

export async function lessonToEnglish(text: string): Promise<string> {
  const res = await translateFn()({ mode: 'lessonToEn', text });
  return String(res.data?.text || '').trim();
}

type AiPoint = { en: string; gu: string; section: number; hold: boolean; type: string };
const toAiPoint = (p: any): AiPoint => ({ en: String(p?.en || ''), gu: String(p?.gu || ''), section: Number.isInteger(p?.section) ? p.section : 1, hold: !!p?.hold, type: String(p?.type || '') });

export async function formalizeLesson(agencyName: string, what: string, suggest?: string): Promise<AiPoint> {
  const res = await translateFn()({ mode: 'formalize', agencyName, what, suggest });
  return toAiPoint(res.data?.point);
}

/** AI rewrites a point someone typed (English and/or Gujarati) in the SOP writing style. */
export async function rewriteInSopStyle(agencyName: string, en: string, gu: string): Promise<AiPoint> {
  const res = await translateFn()({ mode: 'rewrite', agencyName, en, gu });
  return toAiPoint(res.data?.point);
}

/** AI turns pending points into proposals for one agency SOP, using its draft (or approved) points. */
export async function draftWithAi(agencyName: string, current: SopPoint[], common: SopPoint[], pending: SopLesson[]): Promise<SopProposal[]> {
  const point = (p: SopPoint): DraftPoint => ({ id: p.id, section: p.section, text: p.text, hold: p.hold });
  const res = await translateFn()({
    mode: 'draftSop', agencyName, current: current.map(point), common: common.map(point),
    pending: pending.map((l) => ({ id: l.id, text: l.suggest || l.what, context: l.suggest ? l.what : '', impact: l.impact, vendor: l.vendor || '' })),
  });
  const out = res.data?.proposals;
  if (!Array.isArray(out)) throw new Error('AI draft came back empty. Try again.');
  return out as SopProposal[];
}

// ---------- Bilingual PDFs ----------

export type SopPdfRecord = {
  id: string; agencyId: string; projectId: string; projectName: string; version: number; url: string; fileName: string;
  createdAt: string; createdByName: string; firm?: string;
  values?: { en: Record<string, string>; gu: Record<string, string> }; rates?: SopRateRow[];
};

/** Render the PDF on the server from a document-template payload, keep a copy in Storage, and record
 * the version and every value that was filled in. Returns the record and the PDF bytes. */
export async function generateSopPdf(input: {
  payload: Record<string, any>; fileName: string; agencyId: string; projectId: string; projectName: string; version: number;
  byName: string; uid: string; firm: string; values: { en: Record<string, string>; gu: Record<string, string> }; rates: SopRateRow[];
}): Promise<{ record: SopPdfRecord; bytes: Uint8Array }> {
  const render = httpsCallable<any, any>(functions, 'renderTemplatePdf');
  const res = await render(input.payload);
  const base64 = String(res.data?.pdfBase64 || '');
  if (!base64) throw new Error('The PDF could not be made. Try again.');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const path = `sopPdfs/${input.projectId || 'all'}/${input.agencyId}/${input.fileName}_${Date.now()}.pdf`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, bytes, { contentType: 'application/pdf' });
  const url = await getDownloadURL(fileRef);
  const record = {
    agencyId: input.agencyId, projectId: input.projectId, projectName: input.projectName, version: input.version, url, path,
    fileName: `${input.fileName}.pdf`, createdAt: new Date().toISOString(), createdByName: input.byName, createdBy: input.uid,
    firm: input.firm, values: input.values, rates: input.rates, created: serverTimestamp(),
  };
  const added = await addDoc(collection(db, 'sopPdfs'), record);
  return { record: { id: added.id, ...record }, bytes };
}

// ---------- Start checklists: answers per project and agency ----------
//   sopChecklists/{projectId}__{agencyId}  { projectId, agencyId, items: { [itemId]: { value?, done?, by, at } } }

const checklistRef = (projectId: string, agencyId: string) => doc(db, 'sopChecklists', `${projectId}__${agencyId}`);

/** Every agency's start checklist answers for one project, keyed by agency id. */
export function useSopChecklists(projectId: string): Record<string, Record<string, SopCheckAnswer>> {
  const [rows, setRows] = useState<Record<string, Record<string, SopCheckAnswer>>>({});
  useEffect(() => {
    if (!projectId) { setRows({}); return undefined; }
    return onSnapshot(query(collection(db, 'sopChecklists'), where('projectId', '==', projectId)), (snap) => {
      const next: Record<string, Record<string, SopCheckAnswer>> = {};
      snap.docs.forEach((d) => { const data = d.data() as any; next[String(data.agencyId)] = data.items || {}; });
      setRows(next);
    }, () => setRows({}));
  }, [projectId]);
  return rows;
}

export async function loadChecklistAnswers(projectId: string, agencyId: string): Promise<Record<string, SopCheckAnswer>> {
  if (!projectId) return {};
  const snap = await getDoc(checklistRef(projectId, agencyId));
  return snap.exists() ? ((snap.data() as any).items || {}) : {};
}

export async function saveChecklistAnswer(projectId: string, agencyId: string, itemId: string, answer: SopCheckAnswer, byName: string): Promise<void> {
  await setDoc(checklistRef(projectId, agencyId), {
    projectId, agencyId,
    items: { [itemId]: { ...answer, by: byName, at: new Date().toISOString() } },
    updatedAt: serverTimestamp(), updatedBy: byName,
  }, { merge: true });
}

export const SOP_TEMPLATE_GROUP = 'agency_sop';

/** The letterhead for an SOP PDF: the newest "Agency SOP" document template (one assigned to the project wins),
 * the project (logo, background) and its company profile (logo, address, fonts). Template may be null. */
export async function loadSopPdfContext(projectId: string): Promise<{ template: any | null; project: any | null; company: any | null }> {
  const [templatesSnap, projectSnap, companiesSnap] = await Promise.all([
    getDocs(collection(db, 'officeDocumentTemplates')),
    projectId ? getDoc(doc(db, 'projects', projectId)) : Promise.resolve(null),
    getDocs(collection(db, 'companyProfiles')),
  ]);
  const toMillis = (v: any) => (typeof v?.toMillis === 'function' ? v.toMillis() : typeof v?.seconds === 'number' ? v.seconds * 1000 : 0);
  const newest = (rows: any[]) => [...rows].sort((a, b) => (Number(b.version || 0) - Number(a.version || 0)) || (toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt)))[0] || null;
  const candidates = templatesSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))
    .filter((t) => String(t.documentGroupKey || t.visualModel?.documentGroupKey || '').trim() === SOP_TEMPLATE_GROUP || String(t.templateId || '').trim() === 'agency_sop_v1');
  const assigned = candidates.filter((t) => Array.isArray(t.assignedProjectIds) && t.assignedProjectIds.map(String).includes(projectId));
  const project = projectSnap && projectSnap.exists() ? { id: projectSnap.id, ...(projectSnap.data() as any) } : null;
  const companies = companiesSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
  const company = companies.find((c) => c.id === String(project?.companyProfileId || '')) || companies[0] || null;
  return { template: newest(assigned) || newest(candidates), project, company };
}

export function useSopPdfs(agencyId: string | undefined): SopPdfRecord[] {
  const [rows, setRows] = useState<SopPdfRecord[]>([]);
  useEffect(() => {
    if (!agencyId) { setRows([]); return undefined; }
    return onSnapshot(query(collection(db, 'sopPdfs'), where('agencyId', '==', agencyId)), (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as SopPdfRecord)).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))));
    }, () => setRows([]));
  }, [agencyId]);
  return rows;
}

/** Friendly copy for a failed translate call. */
export function translateErrorMessage(err: any): string {
  const code = String(err?.code || '');
  if (code.includes('permission-denied')) return 'You do not have access to AI translation.';
  if (code.includes('resource-exhausted')) return 'Too many translations right now. Try again in a minute.';
  if (code.includes('failed-precondition')) return String(err?.message || 'AI translation is not set up yet.');
  return 'AI could not translate this time. Try again, or type it yourself.';
}

export const isCommon = (id: string) => id === COMMON_SOP_ID;
export type { SopPoint };
